import isSteam from "isSteam";

/**
 * Check an account's features/expacs
 * @param {number} featNum eg 45 for hot
 * @returns {boolean}
 */
function checkAccountFeature(featNum) {
    const { features } = window.native.stats;

    return Boolean(features?.[featNum]?.current);
}

/**
 * Check if app feature exists and is enabled
 * @param {string} featName eg `billing`, `billing-steam`
 * @returns {boolean}
 */
function checkAppFeature(featName) {
    const { features } = GW2.config;

    return !(featName in features) || Boolean(features[featName]);
}

/**
 * Account econ restriction type
 * @returns {"free"|"restricted"|"full"}
 */
function getAccountType() {
    if (!checkAccountFeature(2)) {
        return "free";
    }

    if (window.native.player.isEconomyRestricted) {
        return "restricted";
    }

    return "full";
}

/**
 * Time left with econ restrictions.
 * @returns Time Left in Millis or 0 if no time left.
 */
function getRestrictionTimeLeft() {
    // In Millis
    const restrictionLiftTimestamp = window.native.player.restrictionLiftTime;

    // Fail fast if in the past or unset;
    if (!restrictionLiftTimestamp || restrictionLiftTimestamp < 0) {
        return 0;
    }

    const timeLeft = restrictionLiftTimestamp - Date.now();

    return timeLeft;
}

/**
 * Generates tooltip text with approximate time estimates for when the player will no longer be Econ Restricted.
 * @param {Object} i18nRoot - The loc root object which contains Commerce2's locs.
 * @returns {String|null} - The tooltip string or null when the time estimate cannot be accurately conveyed.
 */
function getRestrictionTimeLeftTooltip(i18nRoot) {
    const durationMillis = getRestrictionTimeLeft();

    // This would be really strange,
    // but returning null prevents the tooltip from displaying.
    // Safer to do that than to communicate a weird error state.
    if (!durationMillis || durationMillis <= 0) {
        return null;
    }

    const millisInMinute = 60 * 1000;
    const millisInHour   = 60 * millisInMinute;
    const millisInDay    = 24 * millisInHour;
    const i18n           = i18nRoot.disabled;

    let count;
    let timeString;

    if (durationMillis >= millisInDay) {
        count      = Math.ceil(durationMillis / millisInDay);
        timeString = count === 1 ? i18n.econRestrictionDay : i18n.econRestrictionDays;
    } else if (durationMillis >= millisInHour) {
        count      = Math.ceil(durationMillis / millisInHour);
        timeString = count === 1 ? i18n.econRestrictionHour : i18n.econRestrictionHours;
    } else {
        count      = Math.ceil(durationMillis / millisInMinute);
        timeString = count === 1 ? i18n.econRestrictionMinute : i18n.econRestrictionMinutes;
    }

    return i18n.econRestriction.replace("${time}", timeString.replace("${count}", count));
}

/**
 * Check if all required app features are enabled for the account type
 * @param {string} feature eg `billing`, `upgrade`
 * @returns {boolean}
 */
function isAppFeatureEnabled(feature) {
    const accountType = getAccountType();

    return (
        // Feature has to be enabled eg `feature.billing`
        checkAppFeature(feature) &&

        // account type level access
        (
            // full accounts always have access
            accountType === "full" ||
            // otherwise check account-type subfield, eg `feature.billing-free`
            checkAppFeature(`${feature}-${accountType}`)
        ) &&

        (
            !isSteam(window.native.stats.login) ||
            checkAppFeature(`${feature}-steam`)
        )

    );
}

/**
 * Check which expansions are unlocked
 * @returns {object} `{ hot : true, hotUltimate : false, ... }`
 */
function getUnlockedExpansions() {
    return {
        hot          : checkAccountFeature(45) || checkAccountFeature(64),
        expac1Deluxe : checkAccountFeature(46),
        hotUltimate  : checkAccountFeature(47),
        expac2       : checkAccountFeature(58),
        expac2Deluxe : checkAccountFeature(62),
        expac3       : checkAccountFeature(65),
        expac3Deluxe : checkAccountFeature(70),
        expac4       : checkAccountFeature(72),
        expac4Deluxe : checkAccountFeature(76),
        expac5       : checkAccountFeature(78),
        expac5Deluxe : checkAccountFeature(82)
    };
}

export {
    getAccountType,
    isAppFeatureEnabled,
    getUnlockedExpansions,
    getRestrictionTimeLeftTooltip
};
